import React from "react";
import styled, { keyframes } from "styled-components";
import contactusvideo from "../../../assets/images/contactUs/contactus.mp4";

// === Animations ===
const floatInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// === Layout ===
const Section = styled.section`
  position: relative;
  width: 100%;
  min-height: 100vh;
  padding: 120px 24px 80px; 
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  /* Soft white and light blue gradient */
  background: linear-gradient(135deg, #ffffff 0%, #e9f4fb 50%, #ffffff 100%);
  font-family: "Poppins", system-ui;
`;

const GlowOrb = styled.div`
  position: absolute;
  width: 420px;
  height: 420px;
  border-radius: 999px;
  /* Brand blue glow */
  background: radial-gradient(circle at 30% 20%, rgba(7, 182, 242, 0.15), transparent);
  top: -120px;
  right: -80px;
  filter: blur(40px);
  pointer-events: none;
`;

const Container = styled.div`
  position: relative;
  width: 100%;
  max-width: 1160px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  z-index: 1;
  animation: ${floatInUp} 0.6s ease-out both;

  @media (min-width: 820px) {
    flex-direction: row;
    align-items: center;
    gap: 40px;
  }
`;

// === Left content ===
const Content = styled.div`
  flex: 1.1;
  color: #0a2a3b; /* Dark ink text */
  text-align: left;
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  /* Light blue pill */
  background: rgba(7, 182, 242, 0.1);
  border: 1px solid rgba(7, 182, 242, 0.3);
  color: #00b5f2;
`;

const Title = styled.h1`
  margin: 18px 0 10px;
  font-size: 2.4rem;
  font-weight: 700;
  line-height: 1.3;
  color: #0a2a3b;

  span {
    color: #07b6f2; /* Brand blue */
  }

  @media (min-width: 768px) {
    font-size: 3rem;
  }

  @media (min-width: 1024px) {
    font-size: 3.4rem;
  }
`;

const AccentBar = styled.div`
  width: 160px;
  height: 4px;
  border-radius: 999px;
  margin: 14px 0 18px;
  background: #07b6f2;
`;

const Description = styled.p`
  font-size: 0.98rem;
  line-height: 1.9;
  color: #5e6b74; /* Muted gray/blue for readability */
  max-width: 520px;
  text-align: justify;

  @media (min-width: 768px) {
    font-size: 1.03rem;
  }
`;

const CTAGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 26px;
`;

const Button = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 12px 24px;
  border-radius: 999px;
  font-size: 0.96rem;
  font-weight: 600;
  text-decoration: none;
  color: #ffffff;
  background: #07b6f2; /* Brand blue background */
  border: none;
  box-shadow: 0 10px 20px rgba(7, 182, 242, 0.25);
  transition: background 0.3s, transform 0.15s ease, box-shadow 0.25s ease;

  i {
    font-size: 0.9rem;
  }

  &:hover {
    background: #06334b; /* Dark ink blue on hover */
    transform: translateY(-2px);
    box-shadow: 0 15px 25px rgba(6, 51, 75, 0.3);
  }
`;

const SubNote = styled.span`
  font-size: 0.8rem;
  color: #8fa0ab;
`;

// === Right video ===
const VideoContainer = styled.div`
  flex: 0.9;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const VideoShell = styled.div`
  width: 100%;
  max-width: 520px;
  border-radius: 26px;
  padding: 10px;
  /* Light glassmorphism border */
  background: rgba(255, 255, 255, 0.6);
  box-shadow: 0 20px 40px rgba(10, 42, 59, 0.08);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(7, 182, 242, 0.2);
`;

const VideoInner = styled.div`
  border-radius: 20px;
  overflow: hidden;
  position: relative;
`;

const Video = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const VideoOverlayTag = styled.div`
  position: absolute;
  bottom: 14px;
  left: 14px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 0.78rem;
  font-weight: 600;
  /* Light overlay tag over the video */
  background: rgba(255, 255, 255, 0.85);
  color: #0a2a3b;
  border: 1px solid rgba(255, 255, 255, 0.5);
`;

// === Component ===
const ContactUsHeader = () => {
  return (
    <Section>
      <GlowOrb />
      <Container>
        <Content>
          <Badge>Contact • NexTech PLM</Badge>
          <Title>
            Unleash your thoughts & ideas. <span>We&apos;re all ears.</span>
          </Title>
          <AccentBar />
          <Description>
            Your thoughts matter to us. Whether you have questions, feedback, or
            are interested in collaboration opportunities, we want to hear from you.
            No question is too big, and no idea is too ambitious. Reach out today,
            and let’s explore how we can connect.
          </Description>
          <CTAGroup>
            <Button href="#contactusform">
              Get In Touch <i className="fa-solid fa-arrow-right" />
            </Button>
            <SubNote>Response time: typically within 1 business day.</SubNote>
          </CTAGroup>
        </Content>
        <VideoContainer>
          <VideoShell>
            <VideoInner>
              <Video autoPlay muted loop playsInline>
                <source src={contactusvideo} type="video/mp4" />
                Your browser does not support the video tag.
              </Video>
              <VideoOverlayTag>Inside NexTech • PLM in action</VideoOverlayTag>
            </VideoInner>
          </VideoShell>
        </VideoContainer>
      </Container>
    </Section>
  );
};

export default ContactUsHeader;